import React, { useMemo } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { Formik } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import { MIME_TYPES } from "config/constants";
import { POLICY_AND_PROCEDURE_V2 } from "config/paths";
import { Breadcrumbs, RequiredFieldAstrick } from "components/Common/Common";
import AppFileUploadField from "components/shared/FormComponents/AppFileUploadField";
import LoadingButton from "components/shared/LoadingButton";
import LoadingIndicator from "components/shared/LoadingIndicator";
import ErrorComponent from "components/shared/ErrorComponent";
import TextInput from "components/shared/FormComponents/V2/commonV2/TextInput";
import { successMessageToast } from "helpers/common";
import { useCreatePolicyMutation, useGetPolicyDetailsQuery, useUpdatePolicyMutation } from "store/services/policies";
import { useTherapistLookupQuery } from "store/services/utility";
import styles from "../../ManageTeam/TherapistProfile/Add/addForm.module.scss";
import CommonButton from "components/shared/FormComponents/V2/commonV2/CommonButton";
import AppInputField from "components/shared/FormComponents/AppInputField";

const defaultValues = { name: "", attachment_key: "", attachment_name: "", audience_type: "global", user_ids: [], therapist_search: "" };
const validationSchema = Yup.object({
  name: Yup.string().trim().required("Policy name is required"),
  attachment_key: Yup.string().required("An attachment is required"),
  audience_type: Yup.string().oneOf(["global", "selected"]).required(),
  user_ids: Yup.array().when("audience_type", { is: "selected", then: (schema) => schema.min(1, "Select at least one therapist") }),
});
const fileNameFromKey = (key = "") => key.split("/").filter(Boolean).pop() || "";

const AddPolicyAndProcedureV2 = () => {
  const { id } = useParams();
  const editMode = Boolean(id);
  const navigate = useNavigate();
  const { data: policyData, isLoading: policyLoading, isError: policyError } = useGetPolicyDetailsQuery(id, { skip: !editMode });
  const { data: therapists = [], isLoading: therapistsLoading } = useTherapistLookupQuery();
  const [createPolicy, { isLoading: isCreating }] = useCreatePolicyMutation();
  const [updatePolicy, { isLoading: isUpdating }] = useUpdatePolicyMutation();

  const initialValues = useMemo(() => {
    // The view endpoint returns the policy under `data` and selected recipients
    // under `assignments`, rather than `user_ids`.
    const policy = policyData?.data || policyData?.policy || policyData || {};
    const assignedUserIds = policy?.assignments
      ?.map((assignment) => assignment?.user_id ?? assignment?.assignedUser?.id)
      .filter((userId) => userId !== null && userId !== undefined);

    return {
      ...defaultValues,
      name: policy?.name || "",
      attachment_key: policy?.attachment_key || policy?.attachment?.attachment_key || "",
      attachment_name: policy?.attachment_name || policy?.attachment?.attachment_name || "",
      audience_type: policy?.audience_type || "global",
      description: policy?.description || "",
      user_ids: (policy?.user_ids || policy?.users?.map((user) => user.id) || assignedUserIds || []).map(Number),
    };
  }, [policyData]);

  const handleSubmit = async (values) => {
    const payload = {
      name: values.name.trim(),
      attachment_key: values.attachment_key,
      attachment_name: values.attachment_name || fileNameFromKey(values.attachment_key),
      audience_type: values.audience_type,
      description: values.description?.trim() || "",
      user_ids: values.audience_type === "global" ? [] : values.user_ids,
    };
    try {
      const result = editMode ? await updatePolicy({ id, data: payload }).unwrap() : await createPolicy(payload).unwrap();
      successMessageToast(result?.message || `Policy ${editMode ? "updated and resent" : "created"} successfully.`);
      navigate(POLICY_AND_PROCEDURE_V2);
    } catch (error) {
      console.error(`Unable to ${editMode ? "update" : "create"} policy`, error);
    }
  };

  const breadcrumbItems = [{ label: "Policies & Procedures", path: POLICY_AND_PROCEDURE_V2 }, { label: editMode ? "Edit Policy" : "Add Policy" }];

  return <section className={`${styles.addForm} position-relative py-0 addForm`}>
    <Container fluid>
      <Row style={{ width: "100%", maxWidth: window.innerWidth >= 992 ? "calc(100% - 170px)" : "100%", padding: "10px" }}>
        <Col lg={12} className="mt-2 mb-3">
          <div className="pageHead d-flex align-items-center justify-content-between flex-wrap"><Breadcrumbs themeBlueV2 items={breadcrumbItems} /></div>
          <h3 className="h5 mt-3">{editMode ? "Edit Policy" : "Add Policy"}</h3>
        </Col>
        {policyLoading ? <LoadingIndicator /> : policyError ? <ErrorComponent /> : <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit} enableReinitialize>
          {({ handleSubmit: submitForm, values, setFieldValue }) => {
            const matchingTherapists = therapists.filter((therapist) => `${therapist?.firstname || ""} ${therapist?.lastname || ""} ${therapist?.email || ""}`.toLowerCase().includes(values.therapist_search.toLowerCase()));
            const toggleTherapist = (therapistId) => setFieldValue("user_ids", values.user_ids.includes(therapistId) ? values.user_ids.filter((userId) => userId !== therapistId) : [...values.user_ids, therapistId]);
            return <Form onSubmit={submitForm} className="mt-0">
              <div>
                <h3 className="h5 mb-0 text-uppercase fw-medium" style={{ color: "#315d99", fontSize: "14px" }}>1. Policy information</h3>
                <div className="py-2" style={{ borderTop: "1px solid #315D99" }}>
                  <Row className="g-3 mt-0">
                    <Col lg={6} md={6} className="mt-0">
                      <label className="discharge-screen-label mb-1">Policy Name <RequiredFieldAstrick /></label>
                      <AppInputField className="readonly-box" name="name" label="Policy Name" required placeholder="Enter policy name" />
                    </Col>
                    <Col lg={6} md={6} className="mt-0">
                      <label className="discharge-screen-label mb-1">Attachment <RequiredFieldAstrick /></label>
                      <AppFileUploadField
                        className="readonly-box"
                        name="attachment_key"
                        acl="private"
                        style={{ padding: "5px " }}
                        accept="application/pdf"
                        allowedTypes={[MIME_TYPES.pdf]}
                        onChange={(_name, key) => { setFieldValue("attachment_key", key); setFieldValue("attachment_name", fileNameFromKey(key)); }}
                      />
                      {/* {values.attachment_name && <small className="text-muted">{values.attachment_name}</small>} */}
                    </Col>

                     <Col lg={10} md={8} className="mt-0">
                      <label className="discharge-screen-label mb-1">Description <RequiredFieldAstrick /></label>
                      <AppInputField fieldType="textarea" rows={4} className="readonly-box" name="description" label="Policy Description" required placeholder="Enter policy description" />
                    </Col>
                  </Row>
                  <Row className="g-3 mt-2"><Col lg={12} className="mt-0"><label className="discharge-screen-label mb-1">Audience <RequiredFieldAstrick /></label><div className="d-flex gap-4 pt-1">{["global", "selected"].map((type) => <Form.Check key={type} inline type="radio" id={`audience-${type}`} label={type === "global" ? "Global" : "Selected therapists"} checked={values.audience_type === type} onChange={() => { setFieldValue("audience_type", type); if (type === "global") setFieldValue("user_ids", []); }} />)}</div></Col></Row>
                </div>
              </div>
              {values.audience_type === "selected" && <div className="mt-4">
                <h3 className="h5 mb-0 text-uppercase fw-medium" style={{ color: "#315d99", fontSize: "14px" }}>2. Select therapists</h3>
                <div className="py-2" style={{ borderTop: "1px solid #315D99" }}>
                  <Row className="g-3 mt-0">
                    <Col lg={6} md={6} className="mt-0">
                      <TextInput name="therapist_search" label="Search therapists" placeholder="Name or email" />
                    </Col>
                  </Row>
                  <div className="table-responsive border" style={{ maxHeight: 340 }}>
                    {therapistsLoading ? <LoadingIndicator /> :
                      <table className="table mb-0">
                        <thead>
                          <tr>
                            <th>
                            </th>
                            <th>Name</th>
                            <th>Email</th>
                          </tr>
                        </thead>
                        <tbody>{matchingTherapists.length ? matchingTherapists.map((therapist) =>
                          <tr key={therapist.id}>
                            <td style={{textAlign: "center"}}><Form.Check aria-label={`Select ${therapist.firstname}`} checked={values.user_ids.includes(Number(therapist.id))} onChange={() => toggleTherapist(Number(therapist.id))} />
                            </td>
                            <td>{therapist.firstname} {therapist.lastname}</td>
                            <td>{therapist.email || "--"}</td></tr>) :
                          <tr><td colSpan="3" className="text-center">No therapists found</td>
                          </tr>}
                        </tbody>
                      </table>}
                  </div>
                  {values.user_ids.length === 0 && <div className="error">Select at least one therapist</div>}
                </div>
              </div>}
              <div className="d-flex justify-content-end gap-2 mt-4">
                <CommonButton style={{ color: "#315D99", background: "#fff" }} text="Cancel" onClick={() => navigate(POLICY_AND_PROCEDURE_V2)}></CommonButton>
                <LoadingButton style={{ color: "#fff", background: "#315D99", borderRadius: "0", borderColor: "#315D99" }} type="submit" isLoading={isCreating || isUpdating} className="commonBtn">{editMode ? "Update & Resend" : "Create Policy"}</LoadingButton></div>
            </Form>;
          }}
        </Formik>}
      </Row>
    </Container>
  </section>;
};

export default AddPolicyAndProcedureV2;
